/**
 * The ROUTINE interior edits, done ON the section row (ADR 0066).
 *
 * The owner tested on a tablet and said section customization is "still
 * difficult/cumbersome". Walking the flow confirmed it: editing one section was
 * Space tab → tap the wall row → Design tab → scroll ~644px past the space
 * height, the platform, the countertop, the target height, the mode toggle and
 * a twenty-button type palette → find the right section row → tap "Interior…" →
 * wait for the modal. Six or seven actions before the first meaningful one.
 *
 * The owner named the edits they actually make: "adding doors, shelf count,
 * drawer count and height", and added drawer FACE HEIGHTS to that list. Those
 * four now live on the row. The modal is unchanged and keeps the complex cases.
 *
 * TWO RULES hold this together, and both are structural rather than
 * conventional — they cannot be forgotten by a later edit:
 *
 *  1. **The row runs the SAME draft helpers the modal runs.** Nothing here
 *     writes an element. Every function below delegates to interior-editor.js
 *     (`draftSetShelfGroupCount`, `draftAddDrawer`, `draftSetDrawerHeight`,
 *     `draftFillSpace`, `draftSetDoors`) and the commit is the modal's own
 *     `applyInteriorDraft`. So heal-on-open (ADR 0065), the structural boards
 *     (ADR 0062), bank seating (ADR 0063), System-32 snapping (ADR 0061) and
 *     the door depth gate (ADR 0060 item 1) all keep working, and the tests
 *     assert composition EQUALITY between a row edit and the modal edit rather
 *     than merely asserting that both look plausible.
 *  2. **A control appears only where its meaning is UNAMBIGUOUS.** See
 *     `inlineInteriorTargets()` — "no guessing" applies to which element a row
 *     control addresses just as much as it applies to identifiers.
 *
 * DOM-free, like interior-editor.js: app.js owns the elements, this owns the
 * semantics, and that is what makes the equality tests possible at all.
 */
import {
  draftAddDrawer,
  draftFillSpace,
  draftRemoveDrawer,
  draftSetDoorExtent,
  draftSetDoors,
  draftSetDrawerHeight,
  draftSetShelfGroupCount,
  draftSpaces,
  draftInsertShelvesIntoOpening,
  shelfRunFor,
} from './interior-editor.js';
import { normalizeInteriorElement } from '../geometry/interior.js';

/**
 * The door coverages offered, in order, with the label each one reads as — ONE
 * list, shared by the modal's button strip and the row's compact select, so a
 * coverage can never be offered in one place and not the other.
 */
export const DOOR_COVERAGE_CHOICES = Object.freeze([
  { value: null, label: 'None' },
  { value: 'all', label: 'All spaces' },
]);

/**
 * The explanation a BLOCKED door control carries (ADR 0060 item 1 as amended:
 * 24" for a section holding a rod anywhere, 4" otherwise).
 *
 * Exported so the modal's buttons and the row's select read from one string.
 * The owner sees the same sentence wherever the rule bites, and a test can
 * assert the two surfaces agree without comparing DOM.
 *
 * @param {{minUnits:number}} depth what doorDepthState() returned
 * @param {(units:number)=>string} [formatLength]
 */
export function doorDepthTitle(depth, formatLength) {
  const min = depth?.minUnits ?? 0;
  return `Needs ${formatLength ? formatLength(min) : `${min}"`} of depth`;
}

/**
 * The ONE space a row control may fill, or null.
 *
 * "Exactly one empty space" is the whole rule, and it is deliberately strict.
 * A DRW section healed by ADR 0062 reads [open space, board, bank, board] — one
 * empty space, so "put 4 shelves above the drawers" is unambiguous and lands on
 * the row. The SH template reads as five shelves between SIX empty spaces;
 * there is no single space a count could mean, so the row offers nothing and
 * the modal's space list — where each space is named with its measured height —
 * is the honest place to choose. Picking "the biggest one" would be a guess,
 * and this project's standing instruction is that we do not make those.
 */
function soleEmptySpace(spaces) {
  const empty = spaces.filter((s) => s.treatment?.key === 'empty');
  return empty.length === 1 ? empty[0] : null;
}

/**
 * WHICH element each row control addresses, resolved from the staged draft.
 *
 * Returns `{ shelves, drawers }`, each either null (no unambiguous target, so
 * the row shows no control) or a descriptor carrying the ELEMENT INDEX the
 * apply functions below act on:
 *
 *   - `{ kind: 'group' }`  — the composition already holds a shelfGroup: the
 *     count edits that group through the ADR 0061 machinery, whose pitch is its
 *     own solved span / N. Note this also FIXES a real divergence: the legacy
 *     Shv field's `applyCountsToInterior` counts discrete `shelf` elements and
 *     is blind to a group, so on a group-bearing section it used to plant loose
 *     boards beside the group instead of changing it.
 *   - `{ kind: 'bank' }`   — the composition holds a drawer bank: the count
 *     adds/removes drawers on it, one at a time, PRESERVING the faces already
 *     chosen (see applyInlineDrawerCount).
 *   - `{ kind: 'fill' }`   — no group/bank, but exactly one empty space: the
 *     count fills that space, which is the modal's own FILL verb (ADR 0065 §4).
 *
 * `count` is what the control should display, and `faces` (banks only) is the
 * per-drawer face list the row's height controls edit.
 *
 * @param {object} draft a draft from createInteriorDraft()
 * @param {object} unit the placed unit the section solves in
 * @param {object} rules resolved business rules
 */
export function inlineInteriorTargets(draft, unit, rules = {}) {
  const none = { shelves: null, drawers: null };
  if (!draft || !unit) return none;
  const spaces = draftSpaces(draft, unit, rules);
  if (!spaces.length) return none;
  const fillable = soleEmptySpace(spaces);

  const group = spaces.find((s) => s.kind === 'shelfGroup') ?? null;
  // A RUN of real boards is the third shape, and now the commonest: "Fill with
  // → Shelves" produces discrete pinned shelves rather than a group, so the
  // row's count field would otherwise find neither a group nor a SOLE empty
  // space and vanish after one use.
  const run = group ? null : shelfRunFor(draft, unit, rules);
  const shelves = group
    ? { kind: 'group', elementIndex: group.elementIndex, count: group.treatment?.count ?? 0 }
    : (run
      ? { kind: 'run', elementIndex: run.indices[0], count: run.count, locked: run.locked, moved: run.moved }
      : (fillable ? { kind: 'fill', elementIndex: fillable.elementIndex, count: 0 } : null));

  const bank = spaces.find((s) => s.kind === 'drawerBank') ?? null;
  const drawers = bank
    ? {
      kind: 'bank',
      elementIndex: bank.elementIndex,
      count: bank.treatment?.count ?? 0,
      faces: (draft.elements[bank.elementIndex]?.drawers ?? []).map((d) => d.heightUnits),
    }
    : (fillable ? { kind: 'fill', elementIndex: fillable.elementIndex, count: 0, faces: [] } : null);

  return { shelves, drawers };
}

/**
 * SHELF COUNT from the row — `draftSetShelfGroupCount` on an existing group,
 * else the modal's FILL verb on the one empty space. Both are the modal's own
 * controls; neither is a second implementation of even spacing.
 *
 * @returns {boolean} false for a junk count or a missing target, so the caller
 *   can restore the field rather than commit nonsense.
 */
export function applyInlineShelfCount(draft, target, count, unit, rules = {}) {
  if (!target || !Number.isInteger(count) || count < 0) return false;
  if (target.kind === 'group') return draftSetShelfGroupCount(draft, target.elementIndex, count);
  if (target.kind === 'run') return applyRunCount(draft, count, unit, rules);
  if (target.kind !== 'fill' || !(count > 0)) return false;
  return draftFillSpace(draft, target.elementIndex, { key: 'shelves', count }, unit, rules);
}

/**
 * Why this REFUSES rather than doing its best.
 *
 * The field means "make this space have N boards", and on a run that re-spaces
 * every board. That is safe only while none of them has a deliberate position:
 * re-spacing moves a board the designer dragged just as surely as shrinking
 * would delete one, so GROWING is not the safe half — there is no safe half.
 *
 * One rule the designer can predict beats two that are individually clever:
 *
 *   - PRISTINE run (nothing locked, every board still where an even fill put
 *     it) → re-fill at N. The common case, and as quick as it ever was.
 *   - ANY board locked or moved → refuse, and say which. Nothing is added,
 *     removed or re-spaced.
 *
 * The alternatives are all worse once positions are deliberate: removing "the
 * last" board is arbitrary, removing the one nearest its even position is
 * unpredictable, and re-spacing everything is the silent-loss class the owner
 * keeps reporting. A refusal costs one extra tap through the Interior editor,
 * where boards can be removed by name; a wrong guess costs work that ruling 4
 * gives no undo for.
 *
 * @returns {true|{ok:false, reason:string}} true when applied; a REASON when
 *   refused, so the row can say why rather than silently snapping back.
 */
function applyRunCount(draft, count, unit, rules) {
  if (!(count > 0)) return false;
  const run = shelfRunFor(draft, unit, rules);
  if (!run) return false;
  if (run.count === count) return false; // nothing to do
  if (run.locked || run.moved) {
    // Name WHICH, because the remedies differ: a locked board is unlocked in a
    // click, while a board positioned by hand is not a mistake to undo — the
    // designer meant it, and the answer is to edit the composition rather than
    // re-space it. "Something is wrong" tells them less than what to do.
    const causes = [];
    if (run.locked) causes.push(`${run.locked} ${run.locked === 1 ? 'board is' : 'boards are'} locked`);
    if (run.moved) causes.push(`${run.moved} ${run.moved === 1 ? 'has' : 'have'} been moved by hand`);
    const remedy = run.locked && !run.moved
      ? 'Unlock them, or change the count in Interior…'
      : 'Changing the count would re-space them. Add or remove a board in Interior… instead.';
    return { ok: false, reason: `Shelf count not changed — ${causes.join(', ')}. ${remedy}` };
  }
  // Pristine: collapse the run back to one opening and fill it afresh.
  const doubleThick = draft.elements[run.indices[0]]?.doubleThick === true;
  const fixed = draft.elements[run.indices[0]]?.fixed === true;
  draft.elements = [normalizeInteriorElement({ kind: 'openSpace' })];
  return draftInsertShelvesIntoOpening(draft, 0, count, unit, rules, { fixed, doubleThick });
}

/**
 * DRAWER COUNT from the row.
 *
 * On an existing BANK the count moves one drawer at a time through
 * `draftAddDrawer` / `draftRemoveDrawer` — the modal's element-list controls —
 * rather than through FILL. Recorded because FILL was the obvious rival and is
 * wrong HERE: filling a space with N drawers re-seeds every face to the tallest
 * standard height that fits (ADR 0065 §4), which would silently discard the
 * face heights the designer set in the control sitting immediately beside this
 * one. Growing repeats the bank's last face; shrinking drops the TOP drawer
 * (index 0), which the bank's own tallest-at-the-bottom sort (ADR 0060 item 6)
 * makes the SMALLEST face. Dropping from the end instead would remove the tall
 * bottom drawer — the one face a designer deliberately chose and the one they
 * would be most upset to lose to a count change.
 *
 * With no bank yet, FILL is exactly right — there are no faces to preserve —
 * and it is what puts the bank in the space the designer means.
 *
 * The structural boards are NOT re-derived here: `applyInteriorDraft` runs
 * `ensureInteriorStructure` at the commit boundary, so a bank still commits
 * sandwiched (ADR 0062) and seated on its shelf (ADR 0063).
 */
export function applyInlineDrawerCount(draft, target, count, unit, rules = {}) {
  if (!target || !Number.isInteger(count) || count < 0) return false;
  if (target.kind === 'fill') {
    return count > 0 && draftFillSpace(draft, target.elementIndex, { key: 'drawers', count }, unit, rules);
  }
  if (target.kind !== 'bank') return false;
  const bank = () => draft.elements[target.elementIndex];
  if (bank()?.kind !== 'drawerBank') return false;
  if (bank().drawers.length === count) return false; // nothing to do; not an error worth committing
  let guard = 0;
  while (bank().drawers.length > count && guard++ < 64) {
    if (!draftRemoveDrawer(draft, target.elementIndex, 0)) return false;
  }
  while (bank().drawers.length < count && guard++ < 64) {
    if (!draftAddDrawer(draft, target.elementIndex, rules)) return false;
  }
  return bank().drawers.length === count;
}

/**
 * ONE drawer's FACE HEIGHT from the row — `draftSetDrawerHeight`, the modal's
 * own setter, so the quarter-inch rounding and the "re-selecting the drawer's
 * own legacy height is a no-op" exception (ADR 0060 item 5) both still hold.
 *
 * `drawerIndex` addresses the bank's list TOP → BOTTOM as it stands BEFORE the
 * edit; the bank re-sorts afterwards (tallest at the bottom, ADR 0060 item 6),
 * which is why the caller must re-render rather than reuse the index.
 */
export function applyInlineDrawerFace(draft, target, drawerIndex, units) {
  if (target?.kind !== 'bank') return false;
  return draftSetDrawerHeight(draft, target.elementIndex, drawerIndex, units);
}

/**
 * DOORS from the row — `draftSetDoors`, unchanged. The DEPTH gate is not
 * re-implemented here either: the caller passes `doorsBlocked` to
 * `applyInteriorDraft`, which refuses the doors write while still committing
 * the interior, exactly as the modal's Apply does.
 */
export function applyInlineDoors(draft, coverage) {
  if (coverage === null) return draftSetDoorExtent(draft, null, null);
  const spaces = (draft?.elements ?? []).filter((el) => typeof el?.id === 'string' && el.id
    && ['openSpace', 'rod', 'shelfGroup', 'shoeShelves', 'drawerBank'].includes(el.kind));
  if (spaces.length) return draftSetDoorExtent(draft, spaces[0].id, spaces.at(-1).id);
  // Compatibility for old un-minted unit fixtures; real model drafts carry ids.
  return draftSetDoors(draft, coverage === 'all' ? 'full' : coverage);
}
